import { createFileRoute, Link } from "@tanstack/react-router";
import {
  CheckCircle2,
  ShieldCheck,
  ArrowRight,
  Award,
  Laptop,
  Briefcase,
  Linkedin,
  Mail,
  Calendar,
} from "lucide-react";
import { CALENDLY_URL } from "@/lib/site";
import { trackScheduleConsultation } from "@/lib/analytics";
import { Reveal } from "@/components/site/Reveal";
import { CtaPanel, PageHero } from "@/components/site/Sections";
import { LeadMagnet } from "@/components/site/LeadMagnet";
import {
  differentiators as differentiatorsData,
  processSteps as processStepsData,
} from "@/lib/finendra-data";
import { useCmsList } from "@/hooks/use-cms";
import teamOffice from "@/assets/team-office.jpg";
import deepakPhoto from "@/assets/deepak-kumar.webp";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About Finendra | Built by Founders, for Founders" },
      {
        name: "description",
        content:
          "Finendra is a bootstrapped finance operations partner founded by Deepak Kumar (9+ years across US, UK, and India accounting). We price so early-stage founders can afford institutional books.",
      },
      { property: "og:title", content: "About Finendra — Built by Founders, for Founders" },
      {
        property: "og:description",
        content:
          "Our founder story, 9+ years of cross-border accounting experience, and why we created transparent, outcome-based pricing for startups.",
      },
      { property: "og:url", content: "https://finendra.com/about" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://finendra.com/og/og-about.png" },
    ],
    links: [{ rel: "canonical", href: "https://finendra.com/about" }],
  }),
  component: AboutPage,
});

const softwareStack = [
  "QuickBooks Online & Desktop",
  "Xero",
  "Sage Intacct",
  "Gusto",
  "Rippling",
  "TriNet",
  "Paylocity",
  "Ramp",
  "Brex",
  "Stripe Billing",
  "Bill.com",
  "Dext",
  "Capium",
  "AutoEntry",
  "A2X",
  "TaxJar",
  "Avalara",
  "Tally Prime",
  "Busy",
  "Zoho Books",
  "Microsoft Excel (Advanced Modeling)",
  "Google Sheets & Apps Script",
];

function AboutPage() {
  const differentiators = useCmsList("differentiator", differentiatorsData);
  const processSteps = useCmsList("process", processStepsData);

  return (
    <>
      <PageHero
        eyebrow="Our Story"
        title="Built by Founders, For Founders."
        body="Finendra is bootstrapped. We don't have VC pressure to upsell you on packages you don't need. We price so early-stage founders can afford us — because we were you."
      />

      {/* Founder Credibility & The Realization */}
      <section className="px-6 pb-24">
        <div className="mx-auto max-w-7xl">
          <div className="grid items-center gap-16 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="relative overflow-hidden rounded-3xl border border-brand-navy/10 bg-brand-surface shadow-xl">
                  <img
                    src={deepakPhoto}
                    alt="Deepak Kumar - Founder & Lead Controller"
                    loading="lazy"
                    width={1024}
                    height={1024}
                    className="aspect-[4/3] w-full object-cover object-[center_20%]"
                  />
                  <div className="p-8 bg-background">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-display text-xl font-bold text-brand-navy">
                          Deepak Kumar
                        </h3>
                        <p className="text-xs font-semibold text-brand-accent">
                          Founder &amp; Controller • B.Com, LL.B.
                        </p>
                      </div>
                      <span className="rounded-full bg-brand-accent/15 px-3 py-1 text-xs font-bold text-brand-navy">
                        9+ Years Experience
                      </span>
                    </div>

                    <div className="mt-3.5 flex flex-wrap items-center gap-2 text-[11px] font-semibold text-brand-navy/75">
                      <span className="inline-flex items-center gap-1 rounded-md bg-brand-surface border border-brand-navy/10 px-2 py-0.5">
                        <Award className="size-3 text-brand-accent" /> Certified QuickBooks
                        ProAdvisor
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-md bg-brand-surface border border-brand-navy/10 px-2 py-0.5">
                        B.Com + LL.B. (Law)
                      </span>
                    </div>

                    <p className="mt-4 text-xs leading-relaxed text-brand-navy/70">
                      Cross-border finance specialist with background spanning premier US startup
                      accounting (Escalon), UK statutory accountancy (SJPR London), and Indian CA
                      statutory audits (Jindal Akhil &amp; Co.). Combines accounting precision with
                      legal (LL.B.) compliance insight for venture-backed tech platforms and global
                      founders.
                    </p>
                  </div>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-7 space-y-6">
              <Reveal>
                <span className="text-xs font-bold uppercase tracking-widest text-brand-accent">
                  The Realization
                </span>
                <h2 className="mt-2 font-display text-3xl font-extrabold text-brand-navy sm:text-4xl">
                  Why early-stage finance ops was broken
                </h2>
                <div className="mt-6 space-y-4 text-base leading-relaxed text-brand-navy/75">
                  <p>
                    Early-stage US founders are caught in an impossible squeeze: pay{" "}
                    <strong>$3,000–$5,000/mo</strong> for big-firm "controller" packages they don't
                    need yet, or pay <strong>$0</strong> and regret it during due diligence when
                    their first term sheet arrives.
                  </p>
                  <p>
                    There was no dependable <strong>$199–$499/mo tier</strong> for businesses that don't need a full-time bookkeeper that actually did the work right: day-one bookkeeping, bank feed hygiene, and delivering clean financials on a predictable monthly cadence.
                  </p>
                  <p className="font-semibold text-brand-navy">
                    Finendra is that tier. Month-to-month. Upgrade as volume expands. No contracts. No surprises.
                  </p>
                </div>

                <div className="mt-8 flex flex-wrap gap-4">
                  <Link
                    to="/pricing"
                    className="inline-flex items-center gap-2 rounded-xl bg-brand-navy px-7 py-3.5 text-sm font-bold text-background shadow-sm transition-[background-color,box-shadow,transform] duration-150 ease-out hover:bg-brand-accent hover:shadow-md hover:-translate-y-0.5 active:scale-[0.975] active:duration-75 motion-reduce:transform-none select-none"
                  >
                    View Our Pricing Tiers <ArrowRight className="size-4" />
                  </Link>
                  <a
                    href={CALENDLY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackScheduleConsultation("about_page_founder_diagnostic")}
                    data-analytics-location="about_page_founder_diagnostic"
                    className="rounded-xl border border-brand-navy/20 bg-background px-7 py-3.5 text-sm font-semibold text-brand-navy transition-[background-color,border-color,transform] duration-150 ease-out hover:bg-brand-surface active:scale-[0.975] active:duration-75 motion-reduce:transform-none select-none inline-flex items-center gap-2"
                  >
                    Schedule Founder Diagnostic <ArrowRight className="size-3.5" />
                  </a>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* 3 Pillars of Global Accounting Expertise */}
      <section className="bg-brand-surface px-6 py-24 border-y border-brand-navy/5">
        <div className="mx-auto max-w-7xl">
          <Reveal className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-accent">
              Core Credentials
            </span>
            <h2 className="mt-2 font-display text-3xl font-bold text-brand-navy sm:text-4xl">
              9+ years of battle-tested accounting operations
            </h2>
            <p className="mt-3 text-base text-brand-navy/60">
              Our founder's background spans enterprise audits, US startup scaling, and UK/India
              cross-border compliance:
            </p>
          </Reveal>

          <div className="mt-12 grid gap-8 md:grid-cols-3">
            <Reveal delay={0.05}>
              <div className="h-full rounded-3xl border border-brand-navy/10 bg-background p-8 shadow-sm">
                <div className="flex size-12 items-center justify-center rounded-2xl bg-brand-accent/10 text-brand-accent mb-6">
                  <Briefcase className="size-6" />
                </div>
                <h3 className="font-display text-xl font-bold text-brand-navy">US Accounting</h3>
                <p className="mt-1 text-xs font-semibold text-brand-accent">
                  Full-Charge Bookkeeping &amp; Controller
                </p>
                <ul className="mt-6 space-y-2.5 text-sm text-brand-navy/70">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-brand-accent shrink-0 mt-0.5" />
                    US tech &amp; venture startup finance ops (Escalon &amp; Avolon pedigree)
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-brand-accent shrink-0 mt-0.5" />
                    Grant expenditure tracking &amp; Single Audit compliance
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-brand-accent shrink-0 mt-0.5" />
                    Multi-state payroll (Gusto, TriNet, Paylocity) &amp; 1099s
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-brand-accent shrink-0 mt-0.5" />
                    Sales tax nexus compliance &amp; ASC 606 revenue rev rec
                  </li>
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="h-full rounded-3xl border border-brand-navy/10 bg-background p-8 shadow-sm">
                <div className="flex size-12 items-center justify-center rounded-2xl bg-brand-accent/10 text-brand-accent mb-6">
                  <Award className="size-6" />
                </div>
                <h3 className="font-display text-xl font-bold text-brand-navy">UK Accounting</h3>
                <p className="mt-1 text-xs font-semibold text-brand-accent">
                  UK GAAP &amp; Statutory Prep
                </p>
                <ul className="mt-6 space-y-2.5 text-sm text-brand-navy/70">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-brand-accent shrink-0 mt-0.5" />
                    UK GAAP compliance &amp; Companies House filings
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-brand-accent shrink-0 mt-0.5" />
                    HMRC VAT returns &amp; Making Tax Digital (MTD)
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-brand-accent shrink-0 mt-0.5" />
                    QuickBooks Online, Xero, Dext, and Capium management
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-brand-accent shrink-0 mt-0.5" />
                    Multi-client portfolio operations and review
                  </li>
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="h-full rounded-3xl border border-brand-navy/10 bg-background p-8 shadow-sm">
                <div className="flex size-12 items-center justify-center rounded-2xl bg-brand-accent/10 text-brand-accent mb-6">
                  <ShieldCheck className="size-6" />
                </div>
                <h3 className="font-display text-xl font-bold text-brand-navy">India CA Firm</h3>
                <p className="mt-1 text-xs font-semibold text-brand-accent">
                  Statutory &amp; External Audit
                </p>
                <ul className="mt-6 space-y-2.5 text-sm text-brand-navy/70">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-brand-accent shrink-0 mt-0.5" />
                    External audits for enterprises (Cipla, Max Fashion, Lifestyle, Torrent)
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-brand-accent shrink-0 mt-0.5" />
                    MCA compliance (DIN, CIN, statutory e-filings, board minutes)
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-brand-accent shrink-0 mt-0.5" />
                    GST, TDS, and corporate ITR tax filings
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-brand-accent shrink-0 mt-0.5" />
                    Tally Prime, ERP 9, Busy, and Zoho Books mastery
                  </li>
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* About Me Section Box */}
      <section className="px-6 py-20 bg-background">
        <div className="mx-auto max-w-5xl">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl border-2 border-brand-accent/20 bg-gradient-to-br from-brand-surface via-background to-brand-accent/5 p-8 md:p-12 shadow-xl shadow-brand-navy/5">
              {/* Header with Badges */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-8 border-b border-brand-navy/10">
                <div className="flex items-center gap-5">
                  <img
                    src={deepakPhoto}
                    alt="Deepak Kumar - Founder & Controller"
                    className="size-20 sm:size-24 rounded-2xl object-cover ring-2 ring-brand-accent/30 shadow-md shrink-0"
                    width={96}
                    height={96}
                  />
                  <div>
                    <div className="inline-flex items-center gap-2 rounded-full bg-brand-accent/15 px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-brand-navy">
                      <Award className="size-3.5 text-brand-accent" />
                      About Me • Founder &amp; Lead Controller
                    </div>
                    <h2 className="mt-2.5 font-display text-2xl font-extrabold text-brand-navy sm:text-3xl lg:text-4xl">
                      Deepak Kumar
                    </h2>
                    <p className="mt-1 text-xs sm:text-sm font-semibold text-brand-accent">
                      Full-Charge Accountant • B.Com, LL.B. • Certified QuickBooks Online ProAdvisor
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2.5 self-start md:self-auto">
                  <a
                    href="https://www.linkedin.com/in/deepak-kumar-8b1a2213b"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-xl border border-brand-navy/15 bg-background px-4 py-2 text-xs font-semibold text-brand-navy shadow-xs transition-colors hover:border-brand-accent hover:text-brand-accent"
                  >
                    <Linkedin className="size-3.5 text-brand-accent" /> LinkedIn Profile
                  </a>
                  <a
                    href="mailto:connect@finendra.com"
                    className="inline-flex items-center gap-1.5 rounded-xl border border-brand-navy/15 bg-background px-4 py-2 text-xs font-semibold text-brand-navy shadow-xs transition-colors hover:border-brand-accent hover:text-brand-accent"
                  >
                    <Mail className="size-3.5 text-brand-accent" /> connect@finendra.com
                  </a>
                </div>
              </div>

              {/* Personal Letter Narrative */}
              <div className="mt-8 space-y-4 text-base leading-relaxed text-brand-navy/80">
                <p>
                  I built Finendra with one guiding conviction: early-stage founders shouldn't have
                  to choose between paying $3,000–$5,000/mo for an enterprise accounting firm or
                  risking costly red flags during their next diligence round.
                </p>
                <p>
                  Over the past 9+ years, I have managed end-to-end accounting operations across
                  three distinct international regimes — the United States, the United Kingdom, and
                  India. My background spans leading high-growth US startup accounting at{" "}
                  <strong>Escalon Business Services</strong>, full-charge bookkeeping and grant
                  compliance at <strong>Avolon</strong> (Atlanta, GA), UK statutory accounts and VAT
                  at <strong>SJPR Accountants</strong> in London, and statutory external audits for
                  enterprises like <strong>Cipla, Max Fashion, Lifestyle, and Torrent</strong> at{" "}
                  <strong>Jindal Akhil &amp; Co.</strong>
                </p>
                <p>
                  Holding degrees in both <strong>Commerce (B.Com)</strong> and{" "}
                  <strong>Law (LL.B.)</strong>, I approach your finances with a dual lens:
                  day-to-day ledger accuracy combined with rigorous corporate compliance, statutory
                  governance, and audit defense.
                </p>
              </div>

              {/* 4 Pillars / Commitments Grid */}
              <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-2xl border border-brand-navy/10 bg-background p-5 shadow-xs">
                  <div className="flex size-8 items-center justify-center rounded-xl bg-brand-accent/15 text-brand-accent mb-3">
                    <ShieldCheck className="size-4" />
                  </div>
                  <h3 className="font-display text-sm font-bold text-brand-navy">
                    Direct Oversight
                  </h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-brand-navy/60">
                    No anonymous junior handoffs. Every close and report is personally reviewed by a
                    named senior controller.
                  </p>
                </div>

                <div className="rounded-2xl border border-brand-navy/10 bg-background p-5 shadow-xs">
                  <div className="flex size-8 items-center justify-center rounded-xl bg-brand-accent/15 text-brand-accent mb-3">
                    <CheckCircle2 className="size-4" />
                  </div>
                  <h3 className="font-display text-sm font-bold text-brand-navy">
                    CPA-Ready Workpapers
                  </h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-brand-navy/60">
                    Schedules and reconciliations built to withstand CPA tax review, diligence, and
                    Single Audits.
                  </p>
                </div>

                <div className="rounded-2xl border border-brand-navy/10 bg-background p-5 shadow-xs">
                  <div className="flex size-8 items-center justify-center rounded-xl bg-brand-accent/15 text-brand-accent mb-3">
                    <Briefcase className="size-4" />
                  </div>
                  <h3 className="font-display text-sm font-bold text-brand-navy">
                    Fixed Retainers
                  </h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-brand-navy/60">
                    Transparent packages starting from $199/mo. Zero hourly surprises, no hidden onboarding
                    fees, month-to-month.
                  </p>
                </div>

                <div className="rounded-2xl border border-brand-navy/10 bg-background p-5 shadow-xs">
                  <div className="flex size-8 items-center justify-center rounded-xl bg-brand-accent/15 text-brand-accent mb-3">
                    <Calendar className="size-4" />
                  </div>
                  <h3 className="font-display text-sm font-bold text-brand-navy">
                    Predictable Rhythm
                  </h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-brand-navy/60">
                    Close delivered by the 5th business day. Proactive communication and responses
                    within one business day.
                  </p>
                </div>
              </div>

              {/* Bottom CTA Row */}
              <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 border-t border-brand-navy/10">
                <p className="text-xs text-brand-navy/60 text-center sm:text-left">
                  Have a question about your books or cross-border structure? Let's talk directly.
                </p>
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackScheduleConsultation("about_page_bottom_cta")}
                  data-analytics-location="about_page_bottom_cta"
                  className="inline-flex items-center gap-2 rounded-xl bg-brand-navy px-6 py-3 text-xs font-bold text-background transition-[background-color,box-shadow,transform] duration-150 ease-out hover:bg-brand-accent hover:shadow-md hover:-translate-y-0.5 active:scale-[0.975] active:duration-75 motion-reduce:transform-none select-none shrink-0"
                >
                  Schedule a Consultation <ArrowRight className="size-3.5" />
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Software Fluency Grid */}
      <section className="px-6 py-20 bg-brand-surface border-y border-brand-navy/5">
        <div className="mx-auto max-w-7xl">
          <Reveal className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-accent">
              Tool Mastery
            </span>
            <h2 className="mt-2 font-display text-3xl font-bold text-brand-navy sm:text-4xl">
              Software fluency
            </h2>
            <p className="mt-3 text-base text-brand-navy/60">
              We work directly inside your platforms from day one:
            </p>
          </Reveal>

          <div className="mt-10 flex flex-wrap gap-2.5">
            {softwareStack.map((tool) => (
              <span
                key={tool}
                className="rounded-xl border border-brand-navy/10 bg-brand-surface px-4 py-2.5 text-xs font-bold text-brand-navy shadow-xs transition-[border-color,background-color,transform] duration-150 ease-out hover:border-brand-accent/40 hover:bg-background hover:-translate-y-0.5 motion-reduce:transform-none"
              >
                {tool}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="bg-brand-navy px-6 py-24 text-background">
        <div className="mx-auto grid max-w-7xl gap-12 md:grid-cols-2">
          <Reveal>
            <h2 className="text-xs font-bold uppercase tracking-widest text-brand-accent">
              Our Mission
            </h2>
            <p className="mt-6 text-2xl font-bold leading-tight text-background">
              To simplify financial operations for growing businesses through dependable accounting
              expertise, modern technology, and founder-accessible pricing.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="text-xs font-bold uppercase tracking-widest text-brand-accent">
              Our Vision
            </h2>
            <p className="mt-6 text-2xl font-bold leading-tight text-background">
              To become the global finance back-office partner of choice for startups, enabling
              founders to build fearlessly while we ensure the numbers are audit-ready.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Lead Magnet */}
      <section className="px-6 py-16 bg-brand-surface">
        <LeadMagnet defaultIndustry="Startup Founder" />
      </section>

      <CtaPanel />
    </>
  );
}
